import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const MultiRailRevenueChart = ({ data, onRailFilter }) => {
  const [activeRails, setActiveRails] = useState({
    bitcoin: true,
    ethereum: true,
    stablecoin: true,
    creditCard: true
  });

  const railConfig = [
    { key: 'bitcoin', label: 'Bitcoin', color: '#F7931A', icon: 'Bitcoin' },
    { key: 'ethereum', label: 'Ethereum', color: '#627EEA', icon: 'Hexagon' },
    { key: 'stablecoin', label: 'Stablecoin', color: '#10B981', icon: 'DollarSign' },
    { key: 'creditCard', label: 'Credit Card', color: '#8B5CF6', icon: 'CreditCard' }
  ];

  const toggleRail = (railKey) => {
    const newActiveRails = { ...activeRails, [railKey]: !activeRails?.[railKey] };
    setActiveRails(newActiveRails);
    if (onRailFilter) {
      onRailFilter(newActiveRails);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="caption text-muted-foreground">{entry?.name}:</span>
              <span className="text-sm font-semibold" style={{ color: entry?.color }}>
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
          <div className="border-t border-border mt-2 pt-2">
            <div className="flex items-center justify-between">
              <span className="caption text-muted-foreground">Total:</span>
              <span className="text-sm font-bold text-foreground">
                ${payload?.reduce((sum, entry) => sum + entry?.value, 0)?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card h-full">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">Multi-Rail Revenue Comparison</h3>
          <p className="caption text-muted-foreground">Revenue distribution across payment rails</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {railConfig?.map((rail) => (
            <button
              key={rail?.key}
              onClick={() => toggleRail(rail?.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-smooth ${
                activeRails?.[rail?.key]
                  ? 'bg-muted text-foreground'
                  : 'bg-muted/30 text-muted-foreground'
              }`}
            >
              <Icon name={rail?.icon} size={16} color={activeRails?.[rail?.key] ? rail?.color : 'currentColor'} />
              <span className="text-sm font-medium">{rail?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-80 md:h-96 lg:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {railConfig?.map((rail) => (
                <linearGradient key={rail?.key} id={`color${rail?.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={rail?.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={rail?.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
            {activeRails?.bitcoin && (
              <Area
                type="monotone"
                dataKey="bitcoin"
                name="Bitcoin"
                stroke="#F7931A"
                fillOpacity={1}
                fill="url(#colorbitcoin)"
                strokeWidth={2}
              />
            )}
            {activeRails?.ethereum && (
              <Area
                type="monotone"
                dataKey="ethereum"
                name="Ethereum"
                stroke="#627EEA"
                fillOpacity={1}
                fill="url(#colorethereum)"
                strokeWidth={2}
              />
            )}
            {activeRails?.stablecoin && (
              <Area
                type="monotone"
                dataKey="stablecoin"
                name="Stablecoin"
                stroke="#10B981"
                fillOpacity={1}
                fill="url(#colorstablecoin)"
                strokeWidth={2}
              />
            )}
            {activeRails?.creditCard && (
              <Area
                type="monotone"
                dataKey="creditCard"
                name="Credit Card"
                stroke="#8B5CF6"
                fillOpacity={1}
                fill="url(#colorcreditCard)"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MultiRailRevenueChart;